import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@ember-data/model';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core-types/symbols';
import type IntlService from 'ember-intl/services/intl';
import { _labelExtract } from 'scaleflow/helpers/label-extract';
import type BetaToggleService from 'scaleflow/services/beta-toggle';
import type OrganisationService from 'scaleflow/services/organisation';
import type StoreService from 'scaleflow/services/store';
import { DateTime } from 'scaleflow/utils/date-time';
import dateSort from 'scaleflow/utils/dateSort';
import localize from 'scaleflow/utils/localize';
import nameSort from 'scaleflow/utils/nameSort';
import type ArtifactModel from './artifact';
import type BonModel from './bon';
import type BookmarkModel from './bookmark';
import type ConversationModel from './conversation';
import type GoalModel from './goal';
import type IdeaModel from './idea';
import type InitiativeDocumentVersionModel from './initiative-document-version';
import { abbreviatedTimeFromNow } from './initiative-update';
import type InitiativeUpdateModel from './initiative-update';
import type LlModel from './ll';
import type PersonModel from './person';
import type RoadmapModel from './roadmap';
import type RoleModel from './role';
import type TargetModel from './target';
import type TeamModel from './team';
import type TfoModel from './tfo';
import type ThemeModel from './theme';
import type UncertaintyModel from './uncertainty';
import type WeekModel from './week';
import type WfaModel from './wfa';
import type WorkStreamModel from './workstream';

export default class InitiativeModel extends Model {
  declare [Type]: 'initiative';
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare betaToggle: BetaToggleService;
  @service('organisation') declare orgService: OrganisationService;

  @hasMany('wfa', { async: true, inverse: 'initiative' })
  declare wfas: AsyncHasMany<WfaModel>;
  @hasMany('uncertainty', { async: true, inverse: 'initiative' })
  declare uncertainties: AsyncHasMany<UncertaintyModel>;
  @hasMany('week', { async: true, inverse: 'initiative' })
  declare weeks: AsyncHasMany<WeekModel>;
  @hasMany('workstream', { async: true, inverse: 'initiative' })
  declare workstreams: AsyncHasMany<WorkStreamModel>;
  @hasMany('bookmark', { async: true, inverse: 'initiative' })
  declare bookmarks: AsyncHasMany<BookmarkModel>;
  @hasMany('tfo', { async: true, inverse: 'initiative' })
  declare tfos: AsyncHasMany<TfoModel>;
  @hasMany('ll', { async: true, inverse: 'initiative' })
  declare lls: AsyncHasMany<LlModel>;
  @hasMany('initiative-document-version', {
    async: true,
    inverse: 'initiative',
  })
  declare document_versions: AsyncHasMany<InitiativeDocumentVersionModel>;
  @hasMany('goal', { async: true, inverse: 'initiatives' })
  declare goals: AsyncHasMany<GoalModel>;

  @belongsTo('bon', { async: true, inverse: 'initiative' })
  declare bon: AsyncBelongsTo<BonModel>;
  @belongsTo('theme', { async: true, inverse: 'initiatives' })
  declare theme: AsyncBelongsTo<ThemeModel>;
  @belongsTo('person', { async: true, inverse: 'initiatives' })
  declare productManager: AsyncBelongsTo<PersonModel>;
  @hasMany('team', { async: true, inverse: 'initiatives' })
  declare teams: AsyncHasMany<TeamModel>;
  @hasMany('roadmap', { async: true, inverse: 'initiatives' })
  declare roadmaps: AsyncHasMany<RoadmapModel>;
  @hasMany('idea', { async: true, inverse: 'initiatives' })
  declare ideas: AsyncHasMany<IdeaModel>;

  @attr('string') declare title: string;
  @attr('string') declare description: string;
  @attr declare summary: unknown;
  @attr('string') declare initiative_doc: string;
  @attr('string') declare approach: string;
  @attr('boolean') declare favorite: boolean;
  @attr('string') declare source: string;
  @attr('string') declare status: string;
  @attr declare text_delta: unknown;
  @attr('string') declare starting_week: string;
  @attr('string') declare start_date: string | undefined;
  @attr('string') declare duration: string;
  @attr('string') declare abbreviation: string;
  @attr('string') declare bon_investment: string;
  @attr('string') declare bon_returns: string;
  @attr('string') declare bon_value: string;
  @attr('string') declare bon_notes: string;
  @attr declare person_weeks: number | undefined;
  @attr declare bon_text_delta: unknown;
  @attr declare progress: string | null;
  @attr declare updated_at: string;

  @hasMany('initiative-update', {
    async: true,
    inverse: 'initiative',
  })
  declare 'initiative-updates': AsyncHasMany<InitiativeUpdateModel>;

  @hasMany('target', {
    async: true,
    inverse: 'initiative',
  })
  declare targets: AsyncHasMany<TargetModel>;

  @hasMany('artifact', {
    async: true,
    inverse: 'initiative',
  })
  declare artifacts: AsyncHasMany<ArtifactModel>;

  @hasMany('conversation', {
    async: true,
    inverse: 'initiative',
  })
  declare conversations: AsyncHasMany<ConversationModel>;

  get formattedBonInvestment() {
    return this.bon_investment == undefined ? '-' : this.bon_investment;
  }

  get formattedBonReturns() {
    return this.bon_returns == undefined ? '-' : this.bon_returns;
  }

  get formattedBonValue() {
    return this.bon_value == undefined ? '-' : this.bon_value;
  }

  get localizedBonInvestment() {
    return this.bon_investment === undefined || this.bon_investment === null
      ? '-'
      : localize(this.bon_investment, ',-', this.intl.t('currency.euro'));
  }

  get localizedBonReturns() {
    return this.bon_returns === undefined || this.bon_returns === null
      ? '-'
      : localize(this.bon_returns, ',-', this.intl.t('currency.euro'));
  }

  get localizedBonValue() {
    return this.bon_value === undefined || this.bon_value === null
      ? '-'
      : localize(this.bon_value, ',-', this.intl.t('currency.euro'));
  }
  get startingWeek() {
    if (!this.startDate) return undefined;
    return DateTime.from(this.startDate).format('GGGG-[W]WW');
  }

  get endingWeek() {
    if (!this.startDate || this.duration == undefined) return undefined;
    return DateTime.from(this.startDate)
      .add(+this.duration, 'weeks')
      .format('GGGG-[W]WW');
  }

  get capacityPerWeek() {
    if (this.person_weeks != undefined && this.duration != undefined) {
      return this.person_weeks / +this.duration;
    } else {
      return this.duration ? +this.duration : 0;
    }
  }

  get personWeekInvestments() {
    if (this.bon_investment == undefined) {
      return [];
    }
    return this.bon.content?.personWeekInvestments;
  }

  get teamIds(): (string | null)[] {
    // if (this.teams?.promise) return this.teams?.content?.map((team) => team.id);
    // return this.teams.map((team) => team.id);
    return (this as InitiativeModel).hasMany('teams')?.ids();
  }

  get goalIds() {
    return this.goals?.content?.map((goal) => goal.id);
  }

  get orderedUpdates() {
    return this['initiative-updates']?.content
      ?.slice()
      ?.sort((a, b) => dateSort(b.created_at, a.created_at));
  }

  get latestUpdate() {
    return this.orderedUpdates?.[0];
  }

  get firstTeam() {
    return [...(this.teams.content ?? [])].sort((a, b) =>
      nameSort(a.name, b.name, 'asc'),
    )[0];
  }

  get firstGoal() {
    return [...(this.goals.content ?? [])].sort((a, b) =>
      nameSort(a.title, b.title, 'asc'),
    )[0];
  }

  get firstRoadmap() {
    return [...(this.roadmaps.content ?? [])].sort((a, b) =>
      nameSort(a.title, b.title, 'asc'),
    )[0];
  }

  get person_weeks_including_uncertainty() {
    const person_week_investments = this.bon.content?.personWeekInvestments;
    if (!person_week_investments) return 0;
    const val = person_week_investments.reduce((acc, item) => {
      const weeks = item.weeks ? +item.weeks : 0;
      const people = item.people ? +item.people : 0;
      const confidence_value = item.confidence_value
        ? +item.confidence_value
        : 1;
      return acc + weeks * people * confidence_value;
    }, 0);
    return val?.toFixed(1);
  }

  get person_weeks_only_uncertainty() {
    const person_week_investments = this.bon.content?.personWeekInvestments;
    if (!person_week_investments) return 0;
    const val = person_week_investments.reduce((acc, item) => {
      const weeks = item.weeks ? +item.weeks : 0;
      const people = item.people ? +item.people : 0;
      const confidence_value = item.confidence_value
        ? +item.confidence_value
        : 1;
      return acc + weeks * people * (confidence_value - 1);
    }, 0);
    return val?.toFixed(1);
  }

  get person_weeks_excluding_uncertainty() {
    return (this.person_weeks ?? 0).toFixed(1);
  }

  get unarchivedUncertainties() {
    return this.uncertainties?.content?.filter(
      (item) => item.archived === false,
    );
  }

  get unarchivedUncertaintiesDetailed() {
    return (this.uncertainties.content ?? [])
      .filter((item) => item.archived === false)
      .reduce(
        (acc, item) => {
          if (item.completed) acc['closed'] += 1;
          else acc['open'] += 1;
          acc['total'] += 1;
          return acc;
        },
        {
          open: 0,
          closed: 0,
          total: 0,
        },
      );
  }

  get unarchivedWfas() {
    return this.wfas?.content?.filter((item) => item.archived === false);
  }

  get unarchivedWfasDetailed() {
    return (this.wfas.content ?? [])
      .filter((item) => item.archived === false)
      .reduce(
        (acc, item) => {
          if (item.completed) acc['closed'] += 1;
          else acc['open'] += 1;
          acc['total'] += 1;
          return acc;
        },
        {
          open: 0,
          closed: 0,
          total: 0,
        },
      );
  }

  get openTickets() {
    let count = 0;
    this.tfos?.content?.forEach((tfo) => {
      const tfo_is_open = tfo.completed === false;
      if (tfo_is_open) count += 1;
    });
    return count;
  }

  get closedTickets() {
    let count = 0;
    this.tfos?.content?.forEach((tfo) => {
      const tfo_is_closed = tfo.completed === true;
      if (tfo_is_closed) count += 1;
    });
    return count;
  }

  get totalTickets() {
    return this.tfos?.content?.length ?? 0;
  }

  get unlistedOpenTickets() {
    return this.tfos?.content?.filter((item) => {
      const workstream = item.get('workstream');
      if (!workstream.isTruthy && item.completed === false) return true;
      else return false;
    }).length;
  }

  get unlistedClosedTickets() {
    return this.tfos?.content?.filter((item) => {
      const workstream = item.get('workstream');
      if (!workstream.isTruthy && item.completed === true) return true;
      else return false;
    }).length;
  }

  get unlistedTickets() {
    return this.tfos?.content?.filter((item) => {
      const workstream = item.get('workstream');
      if (!workstream.isTruthy) return true;
      else return false;
    }).length;
  }

  get roles() {
    const investments = this.personWeekInvestments ?? [];
    const roles: RoleModel[] = [];
    investments.forEach((investment) => {
      if (investment.role_id) {
        const role = this.store.peekRecord<RoleModel>(
          'role',
          investment.role_id,
        );
        if (role) roles.push(role);
      }
    });
    return [...new Set(roles)];
  }

  get closedTicketsScore() {
    return Math.round((+this.closedTickets / +this.totalTickets) * 5);
  }

  get closedTicketsPercentage() {
    if (+this.totalTickets === 0) return 0;
    return Math.round((+this.closedTickets / +this.totalTickets) * 100);
  }

  get statusLabel() {
    return _labelExtract(this.betaToggle.statusOptions, this.status);
  }

  get sourceLabel() {
    return _labelExtract(this.betaToggle.sourceOptions, this.source);
  }

  get expected_health() {
    if (!this.duration || !this.starting_week) return 0;
    const today = DateTime.now();
    const startingWeek = DateTime.from(this.startingWeek);
    const weeks_difference = today.diff(startingWeek, 'weeks');
    const score = Math.ceil((weeks_difference / +this.duration) * 5);
    if (score > 5) return 5;
    if (score <= 0) return 0;
    return score;
  }

  get durationLabel() {
    if (!this.duration) return '-';
    return `~ ${this.duration} ${this.intl.t('dictionary.weeks', {
      num: this.duration,
    })}`;
  }

  get startingDateDDMMMYYY() {
    const start_date = this.startDate;
    if (!start_date) return '-';
    return DateTime.from(start_date).format('DD MMM, YYYY');
  }

  get startingWeekDateObject() {
    const starting_week = this.starting_week;
    if (!starting_week || typeof starting_week !== 'string') return null;

    let date = null;
    const parsed = starting_week.split(':')[0] ?? '';
    const first_day_of_week = this.orgService.first_day_of_week;
    if (first_day_of_week)
      date = DateTime.fromYearWeek(parsed).withDayOfWeek(first_day_of_week);
    else date = DateTime.fromYearWeek(parsed).startOf('week');
    return date.toDate();
  }

  get roadmapIds(): (string | null)[] {
    return (this as InitiativeModel).hasMany('roadmaps')?.ids();
  }

  get orderedUncertainties() {
    return this.uncertainties?.content
      ?.slice()
      ?.sort((a, b) => dateSort(b.created_at, a.created_at));
  }

  get orderedWfas() {
    return this.wfas?.content
      ?.slice()
      ?.sort((a, b) => dateSort(b.created_at, a.created_at));
  }

  get metrics() {
    const targets = this.targets.content ?? [];
    const metrics = targets.map((item) => item.metric);
    return metrics;
  }

  get hasMoreThanOneGoal() {
    const goals = this.goals.content ?? [];
    return goals.length > 1 ? goals.length - 1 : false;
  }

  get openWfas() {
    return (this.wfas.content ?? []).filter((item) => item.completed === false);
  }

  get closedWfas() {
    return (this.wfas.content ?? []).filter((item) => item.completed === true);
  }

  get progressLabel() {
    return this.progress
      ? this.intl.t(`_initiatives.updates_${this.progress.toLowerCase()}`)
      : '';
  }

  get route() {
    return 'backlog.initiatives.initiative.description';
  }

  get attributesStringified() {
    const attributes = [
      this.title,
      this.productManager?.content?.name,
      this.sourceLabel,
      this.statusLabel,
      this.theme?.content?.title,
      this.durationLabel,
      this.startingDateDDMMMYYY,
      this.roadmaps?.content?.map((item) => item.title).join(','),
      this.goals?.content?.map((item) => item.title).join(','),
      this.teams?.content?.map((item) => item.title).join(','),
      this.bon_investment,
      this.bon_value,
      this.bon_returns,
    ];
    return attributes.join(',');
  }

  person_weeks_for_team(id: string | undefined, initiative: InitiativeModel) {
    if (id == 'unassigned') id = undefined;

    const team_id = 'team_id';
    let pw = 0;
    const investments = initiative.bon.content?.investments;

    if (investments !== undefined) {
      investments?.calculations?.forEach((calculation) => {
        if (calculation[team_id] == id) {
          pw +=
            (calculation.people ?? 1) *
            (calculation.weeks ?? 1) *
            (calculation.person_weeks ?? 1) *
            (calculation.confidence_value ?? 1);
        }
      });
    }
    return pw;
  }

  async reorderWeeks() {
    const weeks = this.weeks.content?.slice() ?? [];
    const sorted = weeks.sort((a, b) => a.position - b.position);
    await Promise.all(
      sorted.map((week, i) => {
        week.position = i + 1;
        return week.save();
      }),
    );
  }

  get humanReadableUpdatedAt() {
    return abbreviatedTimeFromNow(this.updated_at);
  }

  get sortedConversations() {
    return [...(this.conversations.content ?? [])].sort((a, b) =>
      dateSort(a.created_at, b.created_at, 'desc'),
    );
  }

  get latestConversation() {
    return this.sortedConversations?.[0] ?? undefined;
  }

  get startDate() {
    if (this.start_date) return this.start_date;
    return this.startingWeekDateObject?.toISOString?.() ?? undefined;
  }

  set startDate(value: string | undefined) {
    this.start_date = value;
    if (value) {
      const from = DateTime.from(value).format('YYYY-WW');
      const to = DateTime.from(value)
        .add(+this.duration || 0, 'weeks')
        .format('YYYY-WW');
      this.starting_week = `${from}:${to}`;
    }
  }

  async save() {
    const wasNew = this.isNew;
    const result = await super.save();
    if (wasNew) {
      await this.createDefaultWorkstreams();
    }
    return result;
  }

  createDefaultWorkstreams() {
    const titles = ['🎯 Week Goals', '⚠️ Events to Consider'];
    return Promise.all(
      titles.map((title, index) =>
        this.store
          .createRecord<WorkStreamModel>('workstream', {
            title,
            position: index + 1,
            initiative: this,
          })
          .save()
          .catch((err) =>
            console.error('Failed to create default workstream', title, err),
          ),
      ),
    );
  }
}
